import AutoModeIcon from '@mui/icons-material/AutoMode';
import CheckIcon from '@mui/icons-material/Check';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import { Box, ListItemButton, Popover, Stack, Typography } from '@mui/material';
import { usePageContext } from 'providers/page';
import { FC, ReactNode, useState } from 'react';
import { SidebarMode } from 'utils/sidebar-mode';

/** Exhaustive, so a new mode has to bring its wording rather than render a blank row. */
const MODES: { mode: SidebarMode; label: string; description: string; icon: ReactNode }[] = [
  {
    mode: 'expanded',
    label: 'Expanded',
    description: 'Always show labels',
    icon: <VerticalSplitIcon fontSize="small" />,
  },
  {
    mode: 'collapsed',
    label: 'Collapsed',
    description: 'Icons only',
    icon: <ViewSidebarIcon fontSize="small" />,
  },
  {
    mode: 'auto',
    label: 'Auto',
    description: 'Wide on main pages; opens on hover inside a campaign',
    icon: <AutoModeIcon fontSize="small" />,
  },
  {
    mode: 'manual',
    label: 'Manual',
    description: 'Hover the nav edge for a handle to open or close',
    icon: <DragHandleIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} />,
  },
];

/** The sidebar's own settings row, pinned to the foot of the nav. */
export const SidebarModeMenu: FC<{ showText: boolean }> = ({ showText }) => {
  const { sidebarMode, setSidebarMode } = usePageContext();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  return (
    <>
      <ListItemButton
        aria-label="Sidebar mode"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          flex: 'none',
          gap: 2,
          px: 3,
          py: 1,
          color: '#D1D5DB',
          backgroundColor: anchor ? 'rgba(255,255,255,.08)' : 'transparent',
          borderTop: '1px solid rgba(255,255,255,.1)',
          '&:hover': { backgroundColor: 'rgba(255,255,255,.06)', color: '#fff' },
        }}
      >
        <Box sx={{ display: 'flex', flex: 'none' }}>
          <ViewSidebarIcon fontSize="small" />
        </Box>
        {showText && (
          <>
            <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
              Sidebar
            </Typography>
            <UnfoldMoreIcon fontSize="small" />
          </>
        )}
      </ListItemButton>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: 320,
              maxHeight: 'calc(100vh - 32px)',
              overflowY: 'auto',
              borderRadius: 2,
              mb: 1,
              p: 1,
            },
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{ display: 'block', px: 1, pt: 0.5, pb: 1, fontWeight: 700, letterSpacing: '.16em' }}
          color="text.secondary"
        >
          SIDEBAR
        </Typography>

        {MODES.map(({ mode, label, description, icon }) => (
          <ListItemButton
            key={mode}
            role="menuitemradio"
            aria-checked={mode === sidebarMode}
            selected={mode === sidebarMode}
            onClick={() => {
              setSidebarMode(mode);
              setAnchor(null);
            }}
            sx={{ alignItems: 'flex-start', gap: 1.5, borderRadius: 2, p: 1.25 }}
          >
            <Box sx={{ display: 'flex', flex: 'none', mt: 0.25 }}>{icon}</Box>
            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700}>
                {label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {description}
              </Typography>
            </Stack>
            {mode === sidebarMode && <CheckIcon fontSize="small" color="error" />}
          </ListItemButton>
        ))}
      </Popover>
    </>
  );
};
